import React, { useState } from 'react';
import { Mail, Lock, Loader2, Shield, Database, Lock as LockIcon, Activity } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-[1000px] bg-white rounded-2xl shadow-xl flex overflow-hidden">
        {/* Left Panel - Content */}
        <div className="hidden md:block w-1/2 bg-blue-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700"></div>
          <div className="absolute inset-0 opacity-30 mix-blend-overlay bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.2),transparent_45%)]"></div>
          
          {/* Content Overlay */}
          <div className="relative h-full flex flex-col justify-center px-8 text-white">
            <div className="mb-8">
              <Shield className="h-12 w-12 mb-4" />
              <h2 className="text-2xl font-bold mb-4">AssetGuard Pro</h2>
              <p className="text-blue-100 mb-6">Secure Asset Management & Monitoring Platform</p>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-3">
                <Database className="h-6 w-6 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-medium mb-1">Asset Guard Pro</h3>
                  <p className="text-sm text-blue-100">Comprehensive tracking and monitoring of industrial assets</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <LockIcon className="h-6 w-6 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-medium mb-1">Enhanced Security</h3>
                  <p className="text-sm text-blue-100">Advanced security protocols and real-time threat detection</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Activity className="h-6 w-6 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-medium mb-1">Real-time Monitoring</h3>
                  <p className="text-sm text-blue-100">24/7 monitoring and instant alerts for critical systems</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="w-full md:w-1/2 p-8 md:p-12">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold text-gray-900">Welcome back</h1>
              <p className="mt-2 text-gray-600">Sign in to your secure dashboard</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    placeholder="Enter your password"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    Remember me
                  </label>
                </div>
                <button type="button" className="text-sm font-medium text-blue-600 hover:text-blue-500">
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </button>
            </form>

            <div className="mt-8 text-center">
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                <Shield className="h-4 w-4" />
                <span>AssetGuard Pro v1.0.0</span>
              </div>
              <div className="mt-1 text-xs text-gray-400">
                © 2025 Quimnel Soft. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};